import * as React from 'react'
import { cn } from '../../lib/utils'

interface WorkspaceShellProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceShell - Main layout container for the workspace.
 */
export function WorkspaceShell({ children, className }: WorkspaceShellProps) {
  return (
    <div
      className={cn(
        'h-screen w-screen flex overflow-hidden bg-background',
        className
      )}
    >
      {children}
    </div>
  )
}

interface WorkspaceSidebarProps {
  children: React.ReactNode
  className?: string
  collapsed?: boolean
  width?: number
}

/**
 * WorkspaceSidebar - Resizable sidebar for the director chat panel.
 */
export function WorkspaceSidebar({
  children,
  className,
  collapsed = false,
  width = 360,
}: WorkspaceSidebarProps) {
  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-card border-r border-border flex flex-col relative transition-all duration-150',
        collapsed ? 'w-14' : '',
        className
      )}
      style={collapsed ? undefined : { width }}
    >
      {children}
    </aside>
  )
}

interface WorkspaceMainProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceMain - Main content area (canvas container).
 */
export function WorkspaceMain({ children, className }: WorkspaceMainProps) {
  return (
    <main className={cn('flex-1 relative overflow-hidden bg-canvas', className)}>
      {children}
    </main>
  )
}

interface WorkspaceHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * WorkspaceHeader - Top header bar for workspace controls.
 */
export function WorkspaceHeader({ children, className }: WorkspaceHeaderProps) {
  return (
    <header
      className={cn(
        'flex items-center justify-between px-4 h-11 border-b border-border bg-card',
        className
      )}
    >
      {children}
    </header>
  )
}

interface SidebarHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * SidebarHeader - Header section of the sidebar.
 */
export function SidebarHeader({ children, className }: SidebarHeaderProps) {
  return (
    <div className={cn('p-3 border-b border-border', className)}>
      {children}
    </div>
  )
}

interface SidebarContentProps {
  children: React.ReactNode
  className?: string
}

/**
 * SidebarContent - Scrollable content area of the sidebar.
 */
export function SidebarContent({ children, className }: SidebarContentProps) {
  return (
    <div className={cn('flex-1 overflow-hidden', className)}>
      {children}
    </div>
  )
}

interface ResizeHandleProps {
  onMouseDown: (e: React.MouseEvent) => void
  className?: string
}

/**
 * ResizeHandle - Draggable handle for resizing panels.
 */
export function ResizeHandle({ onMouseDown, className }: ResizeHandleProps) {
  return (
    <div
      onMouseDown={onMouseDown}
      className={cn(
        'absolute right-0 top-0 bottom-0 w-1 cursor-col-resize z-10',
        'hover:bg-primary/20 transition-colors',
        className
      )}
    />
  )
}

export { WorkspaceShell as default }
